import { Box, Grid, Typography } from '@mui/material'

import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { useGetUniqueAdminCustomers, useGetCustomerFromGovAPI } from 'src/api/customer'

import { AppForm, AppFormProps, FormFileCoreUpload, FormSubmitButton, FormTextField } from 'src/components/FormCore'
import { useZodForm } from 'src/hooks/zod-form'
import { CancelButton } from 'src/components/CancelButton'
import { CustomerCompanyType } from 'src/enums'
import { FormSelectField } from 'src/components/FormCore'
import { AppTextField } from 'src/components/AppTextField'
import { Controller } from 'react-hook-form'

const CustomerForm: React.FC<AppFormProps> = ({ onSubmit, onCancel, initialValues }) => {
  const { t: translate } = useTranslation(['admin-customer', 'common', 'custom-property'])
  const { data } = useGetUniqueAdminCustomers()
  const isUpdateForm = initialValues?.id

  const [orgNo, setOrgNo] = useState('')

  const { data: orgData } = useGetCustomerFromGovAPI(orgNo)

  useEffect(() => {
    if (orgData) {
      form.setValue('name', orgData.Name ?? '')
      form.setValue('address', orgData.Address ?? '')
      form.setValue('zipCode', orgData.PostalCode ?? '')
      form.setValue('city', orgData.City ?? '')
      form.setValue('country', orgData.Country ?? '')
      form.setValue('phone', orgData.Phone ?? '')
    }
  }, [orgData])

  useEffect(() => {
    if (initialValues) {
      form.setValue('externalCustomerNumber', initialValues.externalCustomerNumber ?? '')
    }
  }, [initialValues])
  const handleInputBlur = async event => {
    const inputValue = event.target.value
    if (inputValue.trim()) {
      setOrgNo(inputValue)
    }
  }

  const nameValidation = v => {
    if (isUpdateForm && initialValues?.name == v) {
      return true
    }
    return !data?.find(u => u.name == v)
  }
  type CustomerTypeOption = {
    id: string
    label: string
  }
  const organizationNumberValidation = v => {
    if (isUpdateForm && initialValues?.organizationNr == v) {
      return true
    }
    return !data?.find(u => u.organizationNr == v)
  }

  const customerAddScema = z.object({
    name: z
      .string()
      .refine(nameValidation, { message: translate('duplicateName', { ns: 'custom-property' }) })
      .optional(),
    image: z.string().optional().nullable(),
    fileId: z.union([z.string(), z.number()]).or(z.null()).optional(),
    organizationNr: z
      .string()
      .refine(organizationNumberValidation, {
        message: translate('duplicateOrganizationNumber', { ns: 'custom-property' }),
      })
      .optional(),
    address: z.string().nonempty(),
    zipCode: z.string().nonempty(),
    city: z.string().optional(),
    country: z.string().optional(),
    phone: z.string().optional(),
    isGlobalServiceStationProvider: z.boolean().optional(),
    externalCustomerNumber: z.string().optional(),
  })
  const form = useZodForm(customerAddScema, { defaultValues: initialValues })

  const toTitleCase = (str: string) => {
    return str.replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase())
  }

  return (
    <AppForm form={form} onSubmit={onSubmit}>
      <Grid container>
        <Grid item xs={12} sm={4}>
          <FormTextField
            fullWidth
            name="organizationNr"
            label={translate('organizationNumber')}
            onBlur={handleInputBlur}
          />
          <Typography variant="caption" color="textSecondary" sx={{ margin: 1, display: 'block' }}>
            {translate('org-number-auto-fetch-text')}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={8}>
          <FormSelectField
            name="name"
            label={translate('name', { ns: 'common' })}
            freeSolo
            data={[] as CustomerTypeOption[]}
            getOptionLabel={option => (typeof option === 'string' ? option : option.label)}
            onChange={(_, value, reason, details) => {
              let finalValue = ''

              if (typeof value === 'string') {
                finalValue = value
              } else if (Array.isArray(value)) {
                const first = value[0]
                finalValue = typeof first === 'string' ? first : first?.label ?? ''
              } else if (value) {
                finalValue = value.label
              }
              form.setValue('name', finalValue)
              return finalValue
            }}
            filterOptions={(_, params) => {
              if (
                !(
                  params.inputValue.includes(`${CustomerCompanyType.AS}`) ||
                  params.inputValue.includes(`${CustomerCompanyType.ASA}`)
                )
              ) {
                const inputValue = params.inputValue?.trim()

                if (!inputValue) {
                  return []
                }

                return [
                  {
                    id: `01-${inputValue}`,
                    label: `${toTitleCase(inputValue)} ${CustomerCompanyType.AS}`,
                  },
                  {
                    id: `02-${inputValue}`,
                    label: `${toTitleCase(inputValue)} ${CustomerCompanyType.ASA}`,
                  },
                ]
              } else {
                form.setValue('name', params.inputValue)
                return []
              }
            }}
          />
        </Grid>
        <Grid item xs={12} sm={8}>
          <Grid container>
            <Grid item xs={12} sm={6}>
              <Controller
                name="city"
                control={form.control}
                render={({ field, fieldState }) => (
                  <AppTextField
                    {...field}
                    value={field.value ?? ''}
                    fullWidth
                    label={translate('city', { ns: 'common' })}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="address"
                control={form.control}
                render={({ field, fieldState }) => (
                  <AppTextField
                    {...field}
                    value={field.value ?? ''}
                    fullWidth
                    label={translate('address', { ns: 'common' })}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="country"
                control={form.control}
                render={({ field, fieldState }) => (
                  <AppTextField
                    {...field}
                    value={field.value ?? ''}
                    fullWidth
                    label={translate('country', { ns: 'common' })}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="zipCode"
                control={form.control}
                render={({ field, fieldState }) => (
                  <AppTextField
                    {...field}
                    value={field.value ?? ''}
                    fullWidth
                    label={translate('zipCode', { ns: 'common' })}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="phone"
                control={form.control}
                render={({ field, fieldState }) => (
                  <AppTextField
                    {...field}
                    value={field.value ?? ''}
                    fullWidth
                    label={translate('phone', { ns: 'common' })}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="externalCustomerNumber"
                control={form.control}
                render={({ field, fieldState }) => (
                  <AppTextField
                    {...field}
                    value={field.value ?? ''}
                    fullWidth
                    label={translate('PengVin-Customer-Number', { ns: 'common' })}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormFileCoreUpload
            name="fileId"
            acceptedFileTypes={['image/png', 'image/jpeg', 'image/gif']}
            mapFileKey="id"
            label={translate('image', { ns: 'common' })}
            height="150px"
            userMessage={''}
            iconHeight={'40px'}
            iconWidth={'40px'}
          />
        </Grid>
      </Grid>
      <Box className="flex justify-end gap-4 pt-4">
        <CancelButton onClick={onCancel} />
        <FormSubmitButton
          createText={isUpdateForm ? translate('update', { ns: 'common' }) : translate('add', { ns: 'common' })}
        />
      </Box>
    </AppForm>
  )
}

export default CustomerForm
